import React from 'react';

const getOddsColor = (percent) => {
    if (percent >= 20) return '#9e9e9e';      // Common - grey
    if (percent >= 10) return '#4caf50';      // Uncommon - green
    if (percent >= 5)  return '#2196f3';      // Rare - blue
    if (percent >= 2)  return '#9c27b0';      // Epic - purple
    if (percent >= 0.5) return '#ff9800';     // Legendary - orange
    return '#f44336';                         // Mystic - red
};

const getRarityLabel = (percent) => {
    if (percent >= 20) return 'Common';
    if (percent >= 10) return 'Uncommon';
    if (percent >= 5)  return 'Rare';
    if (percent >= 2)  return 'Epic';
    if (percent >= 0.5) return 'Legendary';
    return 'Mystic';
};

export default function PackOddsModal({ isOpen, onClose, unboxConfig, itemName, itemImg }) {
    if (!isOpen || !unboxConfig) return null;

    const outcomes = unboxConfig.outcomes || [];
    const totalWeight = unboxConfig.totalWeight || outcomes.reduce((sum, o) => sum + (parseInt(o.weight) || 0), 0);
    const rollCount = unboxConfig.rollCount || 1;

    // Sort outcomes by weight descending (most common first)
    const sortedOutcomes = [...outcomes].sort((a, b) => (parseInt(b.weight) || 0) - (parseInt(a.weight) || 0));

    return (
        <div 
            style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.85)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 10000,
                padding: '16px',
                backdropFilter: 'blur(4px)'
            }}
            onClick={onClose}
        >
            <div 
                style={{
                    backgroundColor: '#161922',
                    border: '1px solid #ff9800',
                    borderRadius: '12px',
                    maxWidth: '520px',
                    width: '100%',
                    maxHeight: '90vh',
                    overflowY: 'auto',
                    padding: '24px',
                    boxShadow: '0 0 30px rgba(255, 152, 0, 0.3)',
                    color: '#fff',
                    fontFamily: 'sans-serif'
                }}
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        {itemImg && (
                            <img 
                                src={itemImg} 
                                alt={itemName} 
                                style={{ width: '48px', height: '48px', objectFit: 'contain', borderRadius: '6px', border: '1px solid #333' }} 
                            />
                        )}
                        <div>
                            <h3 style={{ margin: 0, color: '#ff9800', fontSize: '1.25rem' }}>
                                🎲 Drop Rates & Odds
                            </h3>
                            <div style={{ fontSize: '0.85rem', color: '#aaa', marginTop: '2px' }}>
                                {itemName || unboxConfig.unboxName}
                            </div>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        style={{
                            background: 'transparent',
                            border: 'none',
                            color: '#aaa',
                            fontSize: '1.5rem',
                            cursor: 'pointer',
                            padding: '0 6px',
                            lineHeight: 1
                        }}
                    >
                        ×
                    </button>
                </div>

                {/* Subheader info */}
                <div style={{
                    backgroundColor: 'rgba(255, 152, 0, 0.08)',
                    border: '1px solid rgba(255, 152, 0, 0.25)',
                    borderRadius: '6px',
                    padding: '8px 12px',
                    marginBottom: '16px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    fontSize: '0.8rem',
                    color: '#ffc107'
                }}>
                    <span><strong>Rolls Per Open:</strong> {rollCount} Item{rollCount > 1 ? 's' : ''}</span>
                    <span><strong>Total Weight Pool:</strong> {totalWeight}</span>
                </div>

                {/* Outcomes List */}
                <div style={{
                    backgroundColor: 'rgba(0, 0, 0, 0.4)',
                    border: '1px solid #2a2e3d',
                    borderRadius: '8px',
                    padding: '8px 12px',
                    marginBottom: '16px'
                }}>
                    {sortedOutcomes.length === 0 ? (
                        <p style={{ color: '#888', textAlign: 'center', margin: '16px 0' }}>No odds data configured for this pack.</p>
                    ) : (
                        sortedOutcomes.map((outcome, idx) => {
                            const weight = parseInt(outcome.weight) || 0;
                            const percent = totalWeight > 0 ? (weight / totalWeight) * 100 : 0;
                            const displayPercent = percent < 0.01 ? '<0.01' : percent.toFixed(2);
                            const color = getOddsColor(percent);
                            const rarityLabel = getRarityLabel(percent);

                            return (
                                <div 
                                    key={idx}
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '10px',
                                        padding: '8px 0',
                                        borderBottom: idx < sortedOutcomes.length - 1 ? '1px solid rgba(255, 255, 255, 0.06)' : 'none'
                                    }}
                                >
                                    {/* Rarity dot */}
                                    <div style={{
                                        width: '10px',
                                        height: '10px',
                                        borderRadius: '50%',
                                        background: color,
                                        boxShadow: `0 0 8px ${color}`,
                                        flexShrink: 0
                                    }} />

                                    {/* Item name */}
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                        <div style={{ color: '#fff', fontSize: '0.9rem', fontWeight: 'bold', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                            {outcome.name || `Template #${outcome.templateId}`}
                                        </div>
                                        <div style={{ color: '#777', fontSize: '0.72rem' }}>
                                            Weight: {weight}
                                        </div>
                                    </div>

                                    {/* Rarity badge */}
                                    <span style={{
                                        backgroundColor: `${color}20`,
                                        color: color,
                                        border: `1px solid ${color}60`,
                                        fontSize: '0.7rem',
                                        fontWeight: 'bold',
                                        padding: '2px 8px',
                                        borderRadius: '4px',
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.5px',
                                        flexShrink: 0
                                    }}>
                                        {rarityLabel}
                                    </span>

                                    {/* Percentage */}
                                    <span style={{
                                        color: color,
                                        fontSize: '0.95rem',
                                        fontWeight: 'bold',
                                        minWidth: '60px',
                                        textAlign: 'right',
                                        flexShrink: 0
                                    }}>
                                        {displayPercent}%
                                    </span>
                                </div>
                            );
                        })
                    )}
                </div>

                {/* Transparency / Compliance Notice */}
                <div style={{ fontSize: '0.72rem', color: '#888', textAlign: 'center', marginBottom: '16px', lineHeight: 1.4 }}>
                    ⚖️ <em>Drop rates are provably calculated at the time of opening. In compliance with Google Play Store Randomized Virtual Item Policies.</em>
                </div>

                {/* Close Button */}
                <button
                    onClick={onClose}
                    style={{
                        width: '100%',
                        padding: '12px',
                        backgroundColor: '#ff9800',
                        color: '#000',
                        border: 'none',
                        borderRadius: '6px',
                        fontSize: '0.95rem',
                        fontWeight: 'bold',
                        cursor: 'pointer',
                        textTransform: 'uppercase',
                        letterSpacing: '1px'
                    }}
                >
                    Close
                </button>
            </div>
        </div>
    );
}
